<template>
  <div class="awards-tab-section">
    <!-- Enhanced animated background -->
    <div class="bg-decoration">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
      <div class="gradient-orb orb-4"></div>
      <div class="grid-pattern"></div>
    </div>

    <!-- Main content container -->
    <div class="content-wrapper">
      <div class="section-header">
        <h2 class="section-title">
          <span class="title-icon">🏆</span>
          Awards & Recognition
        </h2>
        <p class="section-subtitle">Track your achievements and progress</p>
      </div>

      <!-- Custom tab navigation -->
      <div class="tab-navigation">
        <button
          v-for="(tab) in tabs"
          :key="tab.key"
          :class="['nav-tab', { active: activeTab === tab.key, disabled: tab.disabled }]"
          @click="setActiveTab(tab.key)"
          :disabled="tab.disabled"
        >
          <div class="tab-inner">
            <div class="tab-icon-wrapper">
              <i :class="tab.icon" class="tab-icon"></i>
            </div>
            <div class="tab-info">
              <span class="tab-label">{{ tab.label }}</span>
              <span v-if="tab.count > 0" class="tab-count" :class="tab.countClass">
                {{ tab.count }}
              </span>
            </div>
          </div>
          <div class="tab-glow"></div>
        </button>
      </div>

      <!-- Tab content -->
      <div class="tab-content-wrapper">
        <transition name="tab-slide" mode="out-in">
          <div v-if="activeTab === 'active'" key="active" class="tab-panel">
            <div class="content-card">
              <div class="card-header">
                <h3 class="card-title">
                  <i class="mdi mdi-star-circle-outline"></i>
                  Active Awards
                </h3>
                <div class="card-stats">
                  <span class="stat-label">Total Active:</span>
                  <span class="stat-value">{{ activeAwardsCount }}</span>
                </div>
              </div>
              <div class="card-content">
                <AwardsActiveListSection @update-count="updateActiveCount" />
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'redeemed'" key="redeemed" class="tab-panel">
            <div class="content-card">
              <div class="card-header">
                <h3 class="card-title">
                  <i class="fas fa-gift-open"></i>
                  Redeemed Awards
                </h3>
                <div class="card-stats">
                  <span class="stat-label">Total Redeemed:</span>
                  <span class="stat-value success">{{ redeemedAwardsCount }}</span>
                </div>
              </div>
              <div class="card-content">
                <AwardsReedemedListSectionVue @update-count="updateRedeemedCount" />
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AwardsActiveListSection from "./AwardsActiveListSection.vue";
import AwardsReedemedListSectionVue from "./AwardsReedemedListSection.vue";

export default {
  name: "ModernAwardsTabSection",
  components: {
    AwardsActiveListSection,
    AwardsReedemedListSectionVue
  },
  data() {
    return {
      activeTab: "active",
      activeAwardsCount: 0,
      redeemedAwardsCount: 0
    };
  },
  computed: {
    ...mapGetters({
      profileType: "auth/getType"
    }),
    tabs() {
      const baseTabs = [
        {
          key: "active",
          label: "Active Awards",
          icon: "mdi mdi-star-circle",
          count: this.activeAwardsCount,
          countClass: "primary",
          disabled: false
        }
      ];

      if (this.profileType === "Volunteer") {
        baseTabs.push({
          key: "redeemed",
          label: "Redeemed Awards",
          icon: "mdi mdi-gift-open-outline",
          count: this.redeemedAwardsCount,
          countClass: "success",
          disabled: false
        });
      }

      return baseTabs;
    }
  },
  methods: {
    setActiveTab(tabKey) {
      this.activeTab = tabKey;
    },
    updateActiveCount(count) {
      this.activeAwardsCount = count;
    },
    updateRedeemedCount(count) {
      this.redeemedAwardsCount = count;
    }
  }
};
</script>

<style scoped lang="scss">
// CSS Variables for theming
:root {
  --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  --secondary-gradient: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  --success-gradient: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  --glass-bg: rgba(255, 255, 255, 0.08);
  --glass-border: rgba(255, 255, 255, 0.12);
  --text-primary: #ffffff;
  --text-secondary: rgba(255, 255, 255, 0.8);
  --shadow-primary: 0 20px 40px rgba(0, 0, 0, 0.1);
  --shadow-hover: 0 30px 60px rgba(0, 0, 0, 0.15);
}

.awards-tab-section {
  position: relative;
  min-height: 80vh;
  padding: 2rem;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  // Enhanced background decoration
  .bg-decoration {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;

    .gradient-orb {
      position: absolute;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.4;
      animation: float 20s infinite ease-in-out;

      &.orb-1 {
        width: 400px;
        height: 400px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        top: -200px;
        right: -200px;
        animation-delay: 0s;
      }

      &.orb-2 {
        width: 300px;
        height: 300px;
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        bottom: -150px;
        left: -150px;
        animation-delay: -5s;
      }

      &.orb-3 {
        width: 350px;
        height: 350px;
        background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        animation-delay: -10s;
      }

      &.orb-4 {
        width: 250px;
        height: 250px;
        background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
        bottom: 20%;
        right: 20%;
        animation-delay: -15s;
      }
    }

    .grid-pattern {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: 
        linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
      background-size: 50px 50px;
      animation: grid-move 30s linear infinite;
    }
  }

  .content-wrapper {
    position: relative;
    z-index: 1;
    max-width: 1200px;
    margin: 0 auto;
  }

  .section-header {
    text-align: center;
    margin-bottom: 3rem;

    .section-title {
      font-size: clamp(2rem, 4vw, 3rem);
      font-weight: 700;
      color: #ffff;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;

      .title-icon {
        font-size: 1.2em;
        filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.5));
      }
    }

    .section-subtitle {
      font-size: 1.1rem;
      color: #ffff;
      font-weight: 400;
    }
  }

  .tab-navigation {
    display: flex;
    gap: 1rem;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    justify-content: center;
    color: #fff;
    
    .nav-tab {
      position: relative;
      background: var(--glass-bg);
      backdrop-filter: blur(20px);
      border: 1px solid var(--glass-border);
      border-radius: 1rem;
      padding: 1.5rem 2rem;
      min-width: 200px;
      cursor: pointer;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: var(--shadow-primary);
      overflow: hidden;

      &:hover:not(.disabled) {
        transform: translateY(-5px);
        box-shadow: var(--shadow-hover);
        border-color: rgba(255, 255, 255, 0.2);

        .tab-glow {
          opacity: 1;
        }
      }

      &.active {
        background: linear-gradient(135deg, rgba(5, 25, 115, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
        border: 2px solid #ffff;
        transform: translateY(-2px);

        .tab-glow {
          opacity: 0.8;
        }

        .tab-icon {
          color: #084468;
          transform: scale(1.1);
        }
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .tab-inner {
        display: flex;
        align-items: center;
        gap: 1rem;
        position: relative;
        z-index: 2;
      }

      .tab-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        transition: all 0.3s ease;
      }

      .tab-icon {
        font-size: 1.5rem;
        color: var(--text-primary);
        transition: all 0.3s ease;
      }

      .tab-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
      }

      .tab-label {
        font-size: 1rem;
        font-weight: 600;
        color: var(--text-primary);
        white-space: nowrap;
      }

      .tab-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 24px;
        height: 24px;
        padding: 0 8px;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 700;
        color: white;

        &.primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          animation: pulse-primary 2s infinite;
        }

        &.success {
          background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
        }
      }

      .tab-glow {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
        border-radius: 1rem;
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 1;
      }
    }
  }

  .tab-content-wrapper {
    position: relative;
    min-height: 400px;
  }

  .tab-panel {
    width: 100%;
  }

  .content-card {
    background: var(--glass-bg);
    backdrop-filter: blur(20px);
    border: 1px solid var(--glass-border);
    border-radius: 1.5rem;
    overflow: hidden;
    box-shadow: var(--shadow-primary);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: var(--shadow-hover);
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 2rem;
      border-bottom: 1px solid var(--glass-border);
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid #ffff;
      border-radius: 20px;

      .card-title {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        font-size: 1.5rem;
        font-weight: 600;
        color: #ffff;
        margin: 0;

        i {
          color: #084468;
          font-size: 1.75rem;
        }
      }

      .card-stats {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #084468, #0a5b8a) !important;
        border-radius: 2rem;

        .stat-label {
          font-size: 0.875rem;
          color: #fff;

        }

        .stat-value {
          font-size: 1rem;
          font-weight: 700;
          color: #ffff;

          &.success {
            color: #4ecdc4;
          }
        }
      }
    }

    .card-content {
      padding: 2rem;
    }
  }
}

// Animations
@keyframes float {
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  33% {
    transform: translate(30px, -30px) rotate(120deg);
  }
  66% {
    transform: translate(-20px, 20px) rotate(240deg);
  }
}

@keyframes grid-move {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(50px, 50px);
  }
}

@keyframes pulse-primary {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
  }
}

// Transitions
.tab-slide-enter-active,
.tab-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.tab-slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.tab-slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

// Responsive Design
@media (max-width: 1024px) {
  .awards-tab-section {
    padding: 1.5rem;

    .section-header {
      margin-bottom: 2rem;
    }

    .tab-navigation {
      gap: 0.75rem;

      .nav-tab {
        padding: 1.25rem 1.5rem;
        min-width: 180px;
      }
    }

    .content-card {
      .card-header {
        padding: 1.5rem;
        flex-direction: column;
        gap: 1rem;
        align-items: flex-start;

        .card-stats {
          align-self: stretch;
          justify-content: center;
        }
      }

      .card-content {
        padding: 1.5rem;
      }
    }
  }
}

@media (max-width: 768px) {
  .awards-tab-section {
    padding: 1rem;

    .section-header {
      margin-bottom: 1.5rem;

      .section-title {
        flex-direction: column;
        gap: 0.5rem;
      }
    }

    .tab-navigation {
      flex-direction: column;
      align-items: stretch;

      .nav-tab {
        min-width: unset;
        padding: 1rem 1.25rem;

        .tab-inner {
          justify-content: center;
        }

        .tab-info {
          align-items: center;
        }
      }
    }

    .content-card {
      border-radius: 1rem;

      .card-header {
        padding: 1rem;

        .card-title {
          font-size: 1.25rem;

          i {
            font-size: 1.5rem;
          }
        }
      }

      .card-content {
        padding: 1rem;
      }
    }
  }
}

@media (max-width: 480px) {
  .awards-tab-section {
    padding: 0.75rem;

    .bg-decoration {
      .gradient-orb {
        &.orb-1 {
          width: 200px;
          height: 200px;
          top: -100px;
          right: -100px;
        }

        &.orb-2 {
          width: 150px;
          height: 150px;
          bottom: -75px;
          left: -75px;
        }

        &.orb-3 {
          width: 180px;
          height: 180px;
        }

        &.orb-4 {
          width: 120px;
          height: 120px;
        }
      }
    }

    .tab-navigation {
      .nav-tab {
        padding: 0.875rem 1rem;

        .tab-icon-wrapper {
          width: 40px;
          height: 40px;
        }

        .tab-icon {
          font-size: 1.25rem;
        }

        .tab-label {
          font-size: 0.875rem;
        }
      }
    }
  }
}
</style>